import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../features/auth/authSlice";
import "./LoginPage.css";
import {
  useAdLoginMutation,
  useLoginMutation,
} from "../../features/auth/authApiSlice";
import logo from "../../assets/images/icons/microsoft-logo.svg";
import { sanitizeInputValue } from "../../utils/commonMethods";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const dev = process.env.REACT_APP_ENV === "Development";

  const emailRef = useRef();
  const passwordRef = useRef();

  const [login, { isLoading }] = useAdLoginMutation();
  const [devLogin, { isLoading: devLoginisLoading }] = useLoginMutation();

  const from = location.state?.from?.pathname || "/my-tasks";
  const activeAccounts = instance.getAllAccounts().length;

  useEffect(() => {
    let isMounted = true;
    if (!activeAccounts) return;

    instance
      .handleRedirectPromise()
      .then(async (response) => {
        if (!isMounted || !response) return;
        const requestPromise = login({
          accessToken: response.accessToken,
        }).unwrap();

        toast.promise(requestPromise, {
          loading: "Authenticating...",
          success: "Logged in successfully...",
          error: (err) =>
            `Login failed: ${
              err?.data?.Message || "Unexpected error occured...!"
            }`,
        });

        const userData = await requestPromise;
        if (userData && isMounted) {
          dispatch(setCredentials({ ...userData, isAuthenticatedUser: true }));
          navigate(from, { replace: true });
        }
      })
      .catch((err) => {
        if (isMounted) console.error("Redirect error:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [activeAccounts, login, dispatch, navigate, from, instance]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const email = sanitizeInputValue(emailRef.current.value);
    const password = sanitizeInputValue(passwordRef.current.value);

    try {
      const requestPromise = devLogin({ email, password }).unwrap();
      toast.promise(requestPromise, {
        loading: "Authenticating...",
        success: "Logged in successfully...",
        error: (err) =>
          `Login failed: ${
            err?.data?.Message || "Unexpected error occured...!"
          }`,
      });
      const userData = await requestPromise;
      if (userData) {
        dispatch(setCredentials({ ...userData, isAuthenticatedUser: true }));
        navigate(from, { replace: true });
      }
      emailRef.current.value = "";
      passwordRef.current.value = "";
    } catch (error) {
      toast.error(error?.data?.Message);
    }
  };

  const handleLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      toast.error(error?.message || error?.data?.Message);
      console.error(error);
    }
  };
  if (isLoading) {
    return (
      <div className="container-fluid login">
        <div
          className="row justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="col-12 text-center">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-white">Completing authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid login">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b463d",
            color: "#ffffff",
          },
          success: {
            iconTheme: {
              secondary: "#ffffff",
            },
          },
        }}
      />
      <div
        className="row justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="col-12 col-md-auto col-lg-4 col-xl-4">
          <div className="card shadow-sm mx-auto signin-card">
            <div className="card-body mt-5 mx-4 mb-4">
              <h1 className="text-center fw-bolder mb-5 login-title fs-3">
                Safety Online Reporting Tool
              </h1>
              <div className="d-grid gap-2 mb-4">
                {dev ? (
                  <div>
                    <form className="login-form" onSubmit={handleFormSubmit}>
                      <div className="form-group mb-4">
                        <label htmlFor="email" className="mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          ref={emailRef}
                          placeholder="Email"
                          className="form-control custom-field"
                          required
                        />
                      </div>
                      <div className="form-group mb-4">
                        <label htmlFor="password" className="mb-1">
                          Password
                        </label>
                        <input
                          ref={passwordRef}
                          type="password"
                          name="password"
                          id="password"
                          placeholder="Password"
                          className="form-control custom-field"
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-12">
                          <button
                            disabled={devLoginisLoading}
                            type="submit"
                            className="btn btn-primary w-100 mt-4"
                          >
                            Log In
                          </button>
                        </div>
                      </div>
                    </form>
                    <p className="text-center my-3">----OR----</p>

                    <button
                      disabled={isLoading}
                      onClick={handleLogin}
                      className="btn btn-signin w-100"
                      type="button"
                    >
                      <img
                        className="me-2"
                        src={logo}
                        width={22}
                        alt=""
                        aria-hidden="true"
                      />
                      Sign In with Microsoft
                    </button>
                  </div>
                ) : (
                  <button
                    disabled={isLoading}
                    onClick={handleLogin}
                    className="btn btn-signin"
                    type="button"
                  >
                    <img
                      className="me-2"
                      src={logo}
                      width={22}
                      alt=""
                      aria-hidden="true"
                    />
                    Sign In with Microsoft
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
